import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate as sharedFormatDate, formatTime } from '../../../utils/formatters';
import { supabase } from '../../../lib/supabase';
import { useConfig } from '../../../contexts/ConfigContext';
import { useAuth } from '../../../contexts/AuthContext';
import { usePagePermission } from '../../../hooks/usePagePermission';
import AccessDenied from '../../../components/AccessDenied';
import ErrorDisplay from '../../../components/ErrorDisplay';
import DataTable, { StatusBadge, type Column, type DataRow } from '../../../components/DataTable';
import SearchSelect from '../../../components/SearchSelect';
import Modal from '../../../components/Modal';
import ProposalsBoardView from './proposals/ProposalsBoardView';
import ProposalShareModal from './proposals/ProposalShareModal';
import EditableExpiry from './proposals/EditableExpiry';

interface ProposalLineItem {
  name: string;
  qty: number;
  line_total: number;
}

interface ProposalPricing {
  subtotal?: number;
  total?: number;
  volume_saving?: { amount: number; label?: string };
  voucher_saving?: { amount: number; label?: string };
  tax_amount?: number;
  tax_name?: string;
  tax_rate?: number;
}

interface ProposalData {
  line_items_grouped?: Record<string, ProposalLineItem[]>;
  pricing?: ProposalPricing;
  ambassador?: { name?: string; email?: string };
}

interface Proposal {
  id: string;
  reference_code: string | null;
  customer_name: string | null;
  customer_email: string | null;
  customer_phone: string | null;
  territory_id: string | null;
  total: number | null;
  subtotal: number | null;
  line_items: unknown[] | null;
  proposal_data: ProposalData | null;
  status: string;
  created_at: string;
  expires_at: string | null;
  viewed_at: string | null;
  sent_at: string | null;
  proposal_url: string | null;
  converted_to_booking_id: string | null;
  created_by: string | null;
  source: string | null;
  property_description: string | null;
  property_location: string | null;
  volume_discount_amount: number | null;
  voucher_discount_amount: number | null;
  referral_discount_amount: number | null;
  customer_tier: string | null;
  territories: { name: string } | null;
}

interface StatusConfigEntry {
  label: string;
  bg: string;
  color: string;
  icon: string;
}

function Proposals() {
  const navigate = useNavigate();
  const { formatCurrency } = useConfig();
  const { isSuperAdmin } = useAuth();
  const { canAccess, isLoading: permissionLoading } = usePagePermission('proposals');

  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copyFeedback, setCopyFeedback] = useState<string | null>(null);
  const [viewModal, setViewModal] = useState<Proposal | null>(null);
  const [shareModal, setShareModal] = useState<Proposal | null>(null);
  const [deletingProposal, setDeletingProposal] = useState<Proposal | null>(null);
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Board view state. Persisted view-mode + per-column sort match the
  // POS Proposals pattern (cmbProposalsViewMode / cmbProposalsBoardSorts).
  const [viewMode, setViewMode] = useState<'list' | 'board'>(() => {
    const saved = localStorage.getItem('cmbProposalsViewMode');
    return saved === 'board' ? 'board' : 'list';
  });
  useEffect(() => {
    localStorage.setItem('cmbProposalsViewMode', viewMode);
  }, [viewMode]);

  const [boardSearch, setBoardSearch] = useState('');
  const [boardStatusFilter, setBoardStatusFilter] = useState('');
  const [boardTerritoryFilter, setBoardTerritoryFilter] = useState('');

  const [boardColumnSorts, setBoardColumnSorts] = useState<Record<string, string>>(() => {
    try {
      const saved = localStorage.getItem('cmbProposalsBoardSorts');
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  useEffect(() => {
    localStorage.setItem('cmbProposalsBoardSorts', JSON.stringify(boardColumnSorts));
  }, [boardColumnSorts]);
  const setBoardColumnSort = useCallback((colId: string, sortType: string) => {
    setBoardColumnSorts(prev => ({ ...prev, [colId]: sortType }));
  }, []);

  // Unique territory names from loaded proposals — feeds the board filter.
  const territoryOptions = useMemo(() => {
    const names = Array.from(
      new Set(proposals.map(p => p.territories?.name).filter(Boolean) as string[])
    ).sort((a, b) => a.localeCompare(b));
    return names;
  }, [proposals]);

  // Apply search + filters once for the board. Driven from the same
  // proposals array as the list view so the two never disagree on scope.
  const boardProposals = useMemo(() => {
    const search = boardSearch.trim().toLowerCase();
    return proposals.filter(p => {
      if (search) {
        const haystack = [
          p.reference_code, p.customer_name, p.customer_email, p.customer_phone,
        ].filter(Boolean).join(' ').toLowerCase();
        if (!haystack.includes(search)) return false;
      }
      if (boardStatusFilter && p.status !== boardStatusFilter) return false;
      if (boardTerritoryFilter && p.territories?.name !== boardTerritoryFilter) return false;
      return true;
    });
  }, [proposals, boardSearch, boardStatusFilter, boardTerritoryFilter]);

  const handleDeleteProposal = async () => {
    if (!deletingProposal) return;
    setDeleteSubmitting(true);
    setDeleteError(null);
    try {
      const { error: delError } = await supabase
        .from('estimates')
        .delete()
        .eq('id', deletingProposal.id);
      if (delError) throw delError;
      setDeletingProposal(null);
      setCopyFeedback(`Deleted ${deletingProposal.reference_code || 'proposal'}`);
      setTimeout(() => setCopyFeedback(null), 2500);
      await loadProposals();
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete proposal');
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return sharedFormatDate(date);
  };

  const formatDateTime = (dateStr: string | null): string => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return `${sharedFormatDate(date)} ${formatTime(date)}`;
  };

  // Status badge config
  const statusConfig: Record<string, StatusConfigEntry> = {
    draft: { label: 'Draft', bg: 'var(--color-bg)', color: 'var(--color-text-light)', icon: '📝' },
    sent: { label: 'Sent', bg: 'var(--color-info-bg)', color: 'var(--color-info)', icon: '📤' },
    viewed: { label: 'Viewed', bg: '#f3e8ff', color: 'var(--color-primary-light)', icon: '👁️' },
    accepted: { label: 'Accepted', bg: 'var(--color-success-bg)', color: 'var(--color-success)', icon: '✅' },
    expired: { label: 'Expired', bg: 'var(--color-error-bg)', color: 'var(--color-error)', icon: '⏰' },
    converted: { label: 'Converted', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)', icon: '⭐' },
  };

  const loadProposals = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('estimates')
        .select('id, reference_code, customer_name, customer_email, customer_phone, territory_id, total, subtotal, line_items, proposal_data, status, created_at, expires_at, viewed_at, sent_at, proposal_url, converted_to_booking_id, created_by, source, property_description, property_location, volume_discount_amount, voucher_discount_amount, referral_discount_amount, customer_tier, territories:territory_id(name)')
        .not('proposal_url', 'is', null)
        .order('created_at', { ascending: false })
        .limit(5000);

      if (fetchError) throw fetchError;
      // Supabase returns FK joins as arrays; normalise to single object
      const normalised = (data || []).map((row: Record<string, unknown>) => ({
        ...row,
        territories: Array.isArray(row.territories) ? (row.territories[0] as { name: string } | undefined) ?? null : row.territories,
      })) as Proposal[];
      setProposals(normalised);
    } catch (err: unknown) {
      console.error('Error loading proposals:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canAccess) {
      loadProposals();
    }
  }, [canAccess]);

  const handleCopyLink = (url: string, e?: React.MouseEvent) => {
    if (e) e.stopPropagation();
    navigator.clipboard.writeText(url).then(() => {
      setCopyFeedback('Copied!');
      setTimeout(() => setCopyFeedback(null), 2000);
    });
  };

  // Permission guards
  if (permissionLoading) {
    return (
      <div className="placeholder-page">
        <div className="spinner" />
      </div>
    );
  }

  if (!canAccess) {
    return <AccessDenied feature="proposals" />;
  }

  if (error) {
    return <ErrorDisplay error={error} onRetry={loadProposals} />;
  }

  // Columns
  const columns: Column[] = [
    {
      key: 'reference_code',
      label: 'Reference',
      sortable: true,
      render: (row: DataRow) => (
        <span style={{ fontWeight: 500, fontFamily: 'monospace', fontSize: 'var(--text-base)' }}>
          {(row as unknown as Proposal).reference_code || '-'}
        </span>
      )
    },
    {
      key: 'customer_name',
      label: 'Customer',
      sortable: true,
      render: (row: DataRow) => {
        const r = row as unknown as Proposal;
        return (
          <div>
            <div className="font-medium">{r.customer_name || '-'}</div>
            {r.customer_email && (
              <div className="text-xs text-muted">
                {r.customer_email}
              </div>
            )}
          </div>
        );
      }
    },
    {
      key: 'territory',
      label: 'Territory',
      sortable: true,
      render: (row: DataRow) => (row as unknown as Proposal).territories?.name || '-'
    },
    {
      key: 'total',
      label: 'Total',
      sortable: true,
      align: 'right',
      render: (row: DataRow) => {
        const r = row as unknown as Proposal;
        const incVatTotal = r.proposal_data?.pricing?.total ?? r.total ?? 0;
        return <span className="font-medium">{formatCurrency(incVatTotal)}</span>;
      }
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      align: 'center',
      render: (row: DataRow) => {
        const r = row as unknown as Proposal;
        const st = statusConfig[r.status] ?? statusConfig['draft']!;
        return (
          <StatusBadge bg={st.bg} color={st.color}>
            {st.icon} {st.label}
          </StatusBadge>
        );
      }
    },
    {
      key: 'created_at',
      label: 'Created',
      sortable: true,
      render: (row: DataRow) => formatDate((row as unknown as Proposal).created_at)
    },
    {
      key: 'expires_at',
      label: 'Expires',
      sortable: true,
      render: (row: DataRow) => {
        const r = row as unknown as Proposal;
        if (!r.expires_at) return '-';
        const isExpired = new Date(r.expires_at) < new Date();
        return (
          <span style={{ color: isExpired ? 'var(--color-error)' : undefined }}>
            {formatDate(r.expires_at)}
          </span>
        );
      }
    },
    {
      key: 'viewed_at',
      label: 'Viewed',
      sortable: true,
      render: (row: DataRow) => {
        const r = row as unknown as Proposal;
        return r.viewed_at ? formatDateTime(r.viewed_at) : '';
      }
    },
  ];

  // Filters
  const statusFilterOptions = [
    { value: '', label: 'All Statuses' },
    { value: 'draft', label: 'Draft' },
    { value: 'sent', label: 'Sent' },
    { value: 'viewed', label: 'Viewed' },
    { value: 'accepted', label: 'Accepted' },
    { value: 'expired', label: 'Expired' },
    { value: 'converted', label: 'Converted' },
  ];

  const filters = [
    { key: 'status', label: 'Status', options: statusFilterOptions }
  ];

  // Quick view modal helpers
  const pd: ProposalData = viewModal?.proposal_data || {};
  const lineItemsGrouped = pd.line_items_grouped || {};
  const pricing: ProposalPricing = pd.pricing || {};
  const ambassador: { name?: string; email?: string } = pd.ambassador || {};
  const hasLineItems = Object.keys(lineItemsGrouped).length > 0;
  const viewStatus = viewModal ? (statusConfig[viewModal.status] ?? statusConfig['draft']!) : statusConfig['draft']!;

  return (
    <>
      {copyFeedback && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999,
          background: 'var(--color-success)', color: 'var(--color-white)', padding: '8px 16px',
          borderRadius: 8, fontWeight: 500, fontSize: 'var(--text-base)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          {copyFeedback}
        </div>
      )}
      <div className="page-tabs mb-sm">
        <button
          className={`page-tab ${viewMode === 'list' ? 'active' : ''}`}
          onClick={() => setViewMode('list')}
        >
          List
        </button>
        <button
          className={`page-tab ${viewMode === 'board' ? 'active' : ''}`}
          onClick={() => setViewMode('board')}
        >
          Board
        </button>
      </div>

      {viewMode === 'board' ? (
        <>
          <div className="list-toolbar">
            <div className="list-toolbar-left">
              <div className="list-search">
                <span className="list-search-icon">&#x1F50D;</span>
                <input
                  type="text"
                  placeholder="Search customer, reference, email, phone..."
                  value={boardSearch}
                  onChange={(e) => setBoardSearch(e.target.value)}
                />
                {boardSearch && (
                  <button className="list-search-clear" onClick={() => setBoardSearch('')}>&times;</button>
                )}
              </div>

              <SearchSelect
                width="inline"
                options={statusFilterOptions.map(opt => ({ value: opt.value, label: opt.label }))}
                value={boardStatusFilter}
                onChange={setBoardStatusFilter}
              />

              {territoryOptions.length > 0 && (
                <SearchSelect
                  width="inline"
                  options={[
                    { value: '', label: 'Territory: All' },
                    ...territoryOptions.map(name => ({ value: name, label: name })),
                  ]}
                  value={boardTerritoryFilter}
                  onChange={setBoardTerritoryFilter}
                />
              )}

              {(boardSearch || boardStatusFilter || boardTerritoryFilter) && (
                <button
                  className="btn btn-link"
                  style={{ fontSize: 'var(--text-md)', padding: '0 0.5rem' }}
                  onClick={() => { setBoardSearch(''); setBoardStatusFilter(''); setBoardTerritoryFilter(''); }}
                >
                  Clear filters
                </button>
              )}
            </div>
            <div className="list-toolbar-right">
              <button className="btn btn-secondary" onClick={loadProposals}>Refresh</button>
              <button className="btn btn-primary" onClick={() => navigate('/estimator')}>+ New Proposal</button>
            </div>
          </div>

          <ProposalsBoardView
            proposals={boardProposals as unknown as Record<string, unknown>[]}
            loading={loading}
            formatCurrency={formatCurrency}
            formatDate={(s: string) => formatDate(s)}
            onView={(row) => setViewModal(row as unknown as Proposal)}
            columnSorts={boardColumnSorts}
            onColumnSortChange={setBoardColumnSort}
          />
        </>
      ) : (
      <DataTable
        columns={columns}
        data={proposals as unknown as DataRow[]}
        loading={loading}
        searchable={true}
        searchPlaceholder="Search customer, reference..."
        searchKeys={['customer_name', 'customer_email', 'reference_code']}
        filters={filters}
        defaultFilterValues={{ status: '' }}
        defaultSort={{ key: 'created_at', direction: 'desc' }}
        pageSize={50}
        headerActions={{
          primary: { label: '+ New Proposal', onClick: () => navigate('/estimator') },
          onSync: { label: 'Refresh', onClick: loadProposals }
        }}
        onRowClick={(row: DataRow) => setViewModal(row as unknown as Proposal)}
        actions={(row: DataRow) => {
          const r = row as unknown as Proposal;
          return (
            <>
              <span
                className="table-action-icon"
                onClick={(e) => { e.stopPropagation(); navigate(`/estimator?edit=${r.id}`); }}
                title="Edit in Estimator"
              >
                ✏️
              </span>
              <span
                className="table-action-icon"
                onClick={(e) => { e.stopPropagation(); setViewModal(r); }}
                title="Quick view"
              >
                👁️
              </span>
              {r.proposal_url && (
                <span
                  className="table-action-icon"
                  onClick={(e) => { e.stopPropagation(); setShareModal(r); }}
                  title="Share proposal"
                >
                  📤
                </span>
              )}
              {r.proposal_url && (
                <a
                  href={r.proposal_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="table-action-icon"
                  title="Open proposal"
                  onClick={(e) => e.stopPropagation()}
                >
                  🔗
                </a>
              )}
              {isSuperAdmin && (
                r.converted_to_booking_id ? (
                  <span
                    className="table-action-icon"
                    title="Cannot delete — already converted to a booking"
                    style={{ opacity: 0.3, cursor: 'not-allowed' }}
                    onClick={(e) => e.stopPropagation()}
                  >
                    🗑️
                  </span>
                ) : (
                  <span
                    className="table-action-icon"
                    onClick={(e) => { e.stopPropagation(); setDeletingProposal(r); setDeleteError(null); }}
                    title="Delete proposal"
                  >
                    🗑️
                  </span>
                )
              )}
            </>
          );
        }}
        emptyMessage="No proposals yet. Click '+ New Proposal' to create one."
      />
      )}

      {/* Quick View Modal */}
      {viewModal && (
        <div className="modal-overlay" onClick={() => setViewModal(null)}>
          <div className="modal modal-md" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h2 className="m-0 text-base">{viewModal.reference_code}</h2>
                <div className="text-xs text-muted mt-xs">
                  Created {formatDateTime(viewModal.created_at)}
                </div>
              </div>
              <button className="modal-close" onClick={() => setViewModal(null)}>✕</button>
            </div>

            <div className="modal-body">
              {/* Status + editable expiry on the same row so operators can
                  bump the validity window without opening Share. */}
              <div className="flex items-center justify-between gap-md mb-lg flex-wrap">
                <StatusBadge bg={viewStatus.bg} color={viewStatus.color}>
                  {viewStatus.icon} {viewStatus.label}
                </StatusBadge>
                {viewModal.expires_at && (
                  <EditableExpiry
                    estimateId={viewModal.id}
                    expiresAt={viewModal.expires_at}
                    onChange={(iso) => {
                      setViewModal((prev) => prev ? { ...prev, expires_at: iso } : prev);
                      setProposals((rows) => rows.map(p => p.id === viewModal.id ? { ...p, expires_at: iso } : p));
                    }}
                    compact
                  />
                )}
              </div>

              {/* Customer */}
              <div className="view-section">
                <div className="section-title">Customer</div>
                <div className="font-medium">{viewModal.customer_name || '-'}</div>
                {viewModal.customer_email && <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>{viewModal.customer_email}</div>}
                {viewModal.customer_phone && <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>{viewModal.customer_phone}</div>}
              </div>

              {/* Property */}
              {(viewModal.property_description || viewModal.property_location) && (
                <div className="view-section">
                  <div className="section-title">Property</div>
                  {viewModal.property_description && <div>{viewModal.property_description}</div>}
                  {viewModal.property_location && <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>{viewModal.property_location}</div>}
                </div>
              )}

              {/* Line Items */}
              <div className="view-section">
                <div className="section-title">Scope of Work</div>
                {hasLineItems ? (
                  Object.entries(lineItemsGrouped).map(([category, items]) => (
                    <div key={category} className="mb-md">
                      <div style={{ fontSize: 'var(--text-xs)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-primary)', marginBottom: 4 }}>{category}</div>
                      {items.map((item, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: 'var(--text-base)' }}>
                          <span>{item.name} <span style={{ color: 'var(--color-text-light)' }}>×{item.qty}</span></span>
                          <span style={{ fontWeight: 500 }}>{formatCurrency(item.line_total)}</span>
                        </div>
                      ))}
                    </div>
                  ))
                ) : (
                  <div style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)', fontStyle: 'italic' }}>
                    No line items — edit this proposal to add items
                  </div>
                )}
              </div>

              {/* Pricing */}
              <div className="view-section" style={{ background: 'var(--color-bg-subtle)', borderRadius: 8, padding: '0.75rem' }}>
                <div className="flex justify-between mb-xs">
                  <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>Subtotal</span>
                  <span style={{ fontSize: 'var(--text-base)' }}>{formatCurrency(pricing.subtotal || viewModal.subtotal || 0)}</span>
                </div>
                {(pricing.volume_saving?.amount ?? 0) > 0 && (
                  <div className="flex justify-between mb-xs">
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-success)' }}>{pricing.volume_saving?.label || 'Volume Saving'}</span>
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-success)', fontWeight: 500 }}>−{formatCurrency(pricing.volume_saving!.amount)}</span>
                  </div>
                )}
                {(pricing.voucher_saving?.amount ?? 0) > 0 && (
                  <div className="flex justify-between mb-xs">
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-success)' }}>{pricing.voucher_saving?.label || 'Voucher Saving'}</span>
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-success)', fontWeight: 500 }}>−{formatCurrency(pricing.voucher_saving!.amount)}</span>
                  </div>
                )}
                {(pricing.tax_amount ?? 0) > 0 && (
                  <div className="flex justify-between mb-xs">
                    <span style={{ fontSize: 'var(--text-base)', color: 'var(--color-text-light)' }}>{pricing.tax_name || 'VAT'}{pricing.tax_rate ? ` (${Math.round(pricing.tax_rate * 100)}%)` : ''}</span>
                    <span style={{ fontSize: 'var(--text-base)' }}>{formatCurrency(pricing.tax_amount!)}</span>
                  </div>
                )}
                <div style={{ display: 'flex', justifyContent: 'space-between', borderTop: '2px solid var(--color-primary)', paddingTop: 8, marginTop: 4 }}>
                  <span style={{ fontWeight: 600 }}>Total</span>
                  <span style={{ fontWeight: 700, fontSize: 'var(--text-2xl)', color: 'var(--color-primary)' }}>{formatCurrency(pricing.total || viewModal.total || 0)}</span>
                </div>
              </div>

              {/* Ambassador */}
              {ambassador.name && (
                <div className="view-section">
                  <div className="section-title">Ambassador</div>
                  <div style={{ fontSize: 'var(--text-base)' }}>{ambassador.name}</div>
                  {ambassador.email && <div style={{ fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>{ambassador.email}</div>}
                </div>
              )}

              {/* Dates */}
              <div className="view-section flex gap-xl">
                {viewModal.expires_at && (
                  <div>
                    <div className="section-title">Expires</div>
                    <div style={{ fontSize: 'var(--text-base)' }}>{formatDate(viewModal.expires_at)}</div>
                  </div>
                )}
                {viewModal.viewed_at && (
                  <div>
                    <div className="section-title">First Viewed</div>
                    <div style={{ fontSize: 'var(--text-base)' }}>{formatDateTime(viewModal.viewed_at)}</div>
                  </div>
                )}
                {viewModal.sent_at && (
                  <div>
                    <div className="section-title">Sent</div>
                    <div style={{ fontSize: 'var(--text-base)' }}>{formatDateTime(viewModal.sent_at)}</div>
                  </div>
                )}
              </div>

              {/* Proposal Link */}
              {viewModal.proposal_url && (
                <div className="view-section">
                  <div className="section-title">Proposal Link</div>
                  <div className="flex gap-sm items-center">
                    <input
                      type="text"
                      readOnly
                      value={viewModal.proposal_url}
                      style={{
                        flex: 1, padding: '6px 10px', fontSize: 'var(--text-md)',
                        border: '1px solid var(--color-border)', borderRadius: 6,
                        background: 'var(--color-bg)', fontFamily: 'monospace'
                      }}
                      onClick={e => (e.target as HTMLInputElement).select()}
                    />
                    <button
                      className={`btn btn-sm ${copyFeedback ? 'btn-success' : 'btn-primary'}`}
                      onClick={(e) => handleCopyLink(viewModal.proposal_url!, e)}
                    >
                      {copyFeedback ? '✓' : '📋'}
                    </button>
                  </div>
                </div>
              )}

              {/* Action buttons */}
              <div className="flex gap-sm mt-lg flex-wrap">
                <button
                  className="btn btn-primary"
                  style={{ flex: 1, minWidth: '160px' }}
                  onClick={() => { setViewModal(null); navigate(`/estimator?edit=${viewModal.id}`); }}
                >
                  ✏️ Edit in Estimator
                </button>
                {viewModal.proposal_url && (
                  <button
                    className="btn btn-outline"
                    style={{ flex: 1, minWidth: '140px' }}
                    onClick={() => { const m = viewModal; setViewModal(null); setShareModal(m); }}
                  >
                    📤 Share Proposal
                  </button>
                )}
                {viewModal.proposal_url && (
                  <a
                    href={viewModal.proposal_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-outline"
                    style={{ flex: 1, minWidth: '140px', textAlign: 'center' }}
                  >
                    🔗 Open Proposal
                  </a>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {shareModal && shareModal.proposal_url && (
        <ProposalShareModal
          estimateId={shareModal.id}
          referenceCode={shareModal.reference_code}
          proposalUrl={shareModal.proposal_url}
          expiresAt={shareModal.expires_at}
          customerName={shareModal.customer_name}
          customerEmail={shareModal.customer_email}
          customerPhone={shareModal.customer_phone}
          onClose={() => setShareModal(null)}
        />
      )}

      {deletingProposal && (
        <Modal
          title="Delete proposal?"
          size="sm"
          closeOnOverlay={!deleteSubmitting}
          onClose={() => !deleteSubmitting && setDeletingProposal(null)}
          footer={
            <>
              <button
                className="btn btn-outline"
                onClick={() => setDeletingProposal(null)}
                disabled={deleteSubmitting}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleDeleteProposal}
                disabled={deleteSubmitting}
              >
                {deleteSubmitting ? 'Deleting…' : 'Delete'}
              </button>
            </>
          }
        >
          <p className="m-0 mb-sm text-sm text-light">
            {deletingProposal.reference_code || 'This proposal'} will be permanently removed. This cannot be undone.
          </p>
          {deletingProposal.customer_name && (
            <p className="m-0 mb-md text-sm">
              <strong>{deletingProposal.customer_name}</strong>
              {deletingProposal.customer_email ? ` · ${deletingProposal.customer_email}` : ''}
            </p>
          )}
          {deleteError && (
            <div className="alert-error text-sm">
              {deleteError}
            </div>
          )}
        </Modal>
      )}

    </>
  );
}

export default Proposals;
